// =====================================================================
// PLENARIUM:DUAS_CHAMADAS -- chamada de presenca (1a e 2a)
//  - ChamadaSecretario: passo "Chamada" na tela de Leituras (so leitura)
//  - BotaoSegundaChamada: botao "2a chamada" na tela da sessao (admin)
//  - PainelSegundaChamada: o mesmo painel, no roteiro do Presidente
//  - AvisoSegundaChamada: no tablet, pede a confirmacao da 2a chamada
//  - podeIniciarOrdemDoDia: avisa se a 2a chamada ainda nao foi feita
// Regra: quem nao responde a 2a chamada tem falta na sessao.
// =====================================================================
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useEstruturaSessao, quorumDaCamara, carregarEstruturaSessao } from './EstruturaSessao'; // PLENARIUM:QUORUM_CAMARA PLENARIUM:PRESENCA_REGISTRO
import { io } from 'socket.io-client';
import { UserCheck, X, Check, Lock, Unlock, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const erroDe = (e, padrao) => (e && e.response && e.response.data && e.response.data.error) || padrao;

// Recarrega quando a presenca ou a chamada mudam (sala da sessao e da camara)
function useAoVivo(sessaoId, camaraId, recarregar) {
  useEffect(() => {
    if (!sessaoId && !camaraId) return undefined;
    const s = io(API);
    let t = null;
    const agendar = () => { clearTimeout(t); t = setTimeout(recarregar, 350); };
    const entrar = () => {
      if (sessaoId) s.emit('entrar-sala', `sessao-${sessaoId}`);
      if (camaraId) s.emit('entrar-sala', `camara-${camaraId}`);
    };
    // PLENARIUM:CHAMADA_RECONECTAR -- a conexao voltou (ou o tablet acordou): confere de novo na hora
    let jaConectou = false;
    s.on('connect', () => { entrar(); if (jaConectou) agendar(); jaConectou = true; });
    const aoVoltar = () => { if (document.visibilityState === 'visible') agendar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('online', agendar);
    ['presenca-atualizada', 'chamada-atualizada', 'sessao-atualizada', 'pauta-encerrada'].forEach(ev => s.on(ev, agendar));
    return () => { clearTimeout(t); document.removeEventListener('visibilitychange', aoVoltar); window.removeEventListener('online', agendar); s.disconnect(); };
  }, [sessaoId, camaraId, recarregar]);
}

// Lista de presenca (1a e 2a) + estado da 2a chamada
export function useChamadas(sessaoId) {
  const [d, setD] = useState(null);
  const carregar = useCallback(async () => {
    if (!sessaoId) return;
    try {
      const [rel, ch] = await Promise.all([
        axios.get(`/api/relatorios/sessao/${sessaoId}`),
        axios.get(`/api/chamadas/sessao/${sessaoId}`).catch(() => ({ data: { duas: false, segunda: null, presentes2: [] } }))
      ]);
      const lista = (rel.data.presenca || []).map(p => ({
        ...p,
        p1: p.presente1 != null ? !!p.presente1 : !!p.presente,
        o1: p.origem1 || (p.presentePorVoto ? 'VOTO' : p.presenteManual ? 'TABLET' : null),
        p2: !!p.presente2,
        o2: p.origem2 || null
      })).sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
      setD({ lista, duas: !!ch.data.duas, segunda: ch.data.segunda || rel.data.segundaChamada || null, camaraId: rel.data.camaraId, status: rel.data.status });
    } catch (e) { setD(x => x || { erro: true, lista: [] }); }
  }, [sessaoId]);
  useEffect(() => { carregar(); }, [carregar]);
  useAoVivo(sessaoId, d && d.camaraId, carregar);
  return [d, carregar];
}

const ORIGEM = { TABLET: 'confirmou no tablet', MANUAL: 'registro da Mesa', VOTO: 'votou' };
const quorum = (n) => Math.floor(n / 2) + 1;
// PLENARIUM:QUORUM_CAMARA -- 1a chamada: quorum de abertura; 2a chamada (antes da Ordem do Dia): de votacao

// >>> PLENARIUM:PRESENCA_REGISTRO -- historico da presenca (o registro do vereador nunca e apagado)
export function useHistoricoPresenca(sessaoId, ativo, dep) {
  const [h, setH] = useState([]);
  useEffect(() => {
    if (!sessaoId || !ativo) return;
    axios.get(`/api/presencas/${sessaoId}/historico`).then(r => setH(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [sessaoId, ativo, dep]);
  return h;
}
const horaH = (d) => { try { return new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
const ROT_H = { CONFIRMOU: '📱 confirmou', MESA_COLOCOU: '🖊️ Mesa colocou', MESA_RETIROU: '✖ Mesa retirou' };
export function HistoricoPresenca({ itens, vereadorId, chamada }) {
  const l = (itens || []).filter(x => x.vereadorId === vereadorId && Number(x.chamada) === chamada);
  if (!l.length) return null;
  return (
    <small style={{ display: 'block', color: '#64748b', fontSize: '.72rem', marginTop: 2 }}>
      {l.map((x, i) => <span key={i}>{i ? ' · ' : ''}{ROT_H[x.acao] || x.acao} {horaH(x.criadoEm)}{x.acao !== 'CONFIRMOU' && x.por ? ` (${x.por})` : ''}</span>)}
    </small>
  );
}

// Confere o quorum da Camara antes de seguir. Abaixo do minimo: AVISA e o
// Presidente decide (o roteiro e a ata registram a falta de quorum).
export async function confirmaQuorum(sessaoId, fase) {
  try {
    const [r, est] = await Promise.all([axios.get(`/api/presencas/painel/${sessaoId}`), carregarEstruturaSessao()]);
    const l = (r.data && r.data.presenca) || [];
    if (!l.length) return true;
    const n = fase === 'abertura' ? l.filter(p => (p.presente1 != null ? p.presente1 : p.presente)).length : l.filter(p => p.presente).length;
    const min = quorumDaCamara(est, fase, quorum(l.length));
    if (n > 0 && n < min) {
      return window.confirm(fase === 'abertura'
        ? `Quórum para ABRIR a sessão não atingido: ${n} presente(s), mínimo de ${min}.\n\nPelo Regimento, a sessão não deve ser aberta. O roteiro e a ata registram a falta de quórum.\n\nContinuar mesmo assim?`
        : `Quórum para VOTAR não atingido: ${n} presente(s), mínimo de ${min}.\n\nSem quórum, as matérias devem ficar adiadas. O roteiro e a ata registram isso.\n\nIniciar a Ordem do Dia mesmo assim?`);
    }
  } catch (e) { /* sem a informacao, segue como antes */ }
  return true;
}
// <<< PLENARIUM:PRESENCA_REGISTRO

const CSS = `
.pch { font-family: inherit; color: #0f172a; }
.pch-abas { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.pch-aba { flex: 1; min-width: 140px; border: 2px solid #e2e8f0; background: #fff; border-radius: 12px; padding: 10px 12px; font: inherit; font-weight: 800; cursor: pointer; color: #0f172a; text-align: left; }
.pch-aba small { display: block; font-weight: 600; color: #64748b; font-size: .78rem; }
.pch-aba.on { border-color: #1d4ed8; background: #eff6ff; }
.pch-aba .pch-vivo { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #dc2626; margin-right: 6px; animation: pchPulso 1.2s infinite; vertical-align: middle; }
@keyframes pchPulso { 0%,100% { opacity: 1 } 50% { opacity: .3 } }
.pch-placar { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.pch-num { flex: 1; min-width: 120px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 14px; }
.pch-num b { display: block; font-size: 1.7rem; line-height: 1.1; }
.pch-num span { font-size: .78rem; color: #64748b; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; }
.pch-num.ok b { color: #15803d; } .pch-num.no b { color: #b91c1c; }
.pch-lista { display: flex; flex-direction: column; gap: 6px; }
.pch-linha { display: flex; align-items: center; gap: 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 14px; }
.pch-linha.pres { border-color: #86efac; background: #f0fdf4; }
.pch-n { width: 28px; height: 28px; border-radius: 8px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: .85rem; color: #475569; flex-shrink: 0; }
.pch-nome { flex: 1; min-width: 0; }
.pch-nome b { display: block; font-size: 1.08rem; text-transform: uppercase; letter-spacing: .01em; }
.pch-nome small { color: #64748b; font-size: .8rem; }
.pch-selo { flex-shrink: 0; border-radius: 999px; padding: 4px 12px; font-size: .78rem; font-weight: 800; white-space: nowrap; }
.pch-selo.sim { background: #16a34a; color: #fff; }
.pch-selo.nao { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }
.pch-selo.just { background: #fef3c7; color: #92400e; }
.pch-selo.falta { background: #fee2e2; color: #b91c1c; }
.pch-dica { font-size: .85rem; color: #475569; background: #f8fafc; border-radius: 10px; padding: 10px 12px; margin-bottom: 12px; }
.pch-vazio { text-align: center; color: #64748b; padding: 26px 12px; background: #f8fafc; border-radius: 12px; }
.pch-bts { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.pch-bt { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #cbd5e1; background: #fff; border-radius: 10px; padding: 9px 14px; font: inherit; font-weight: 700; cursor: pointer; color: #0f172a; }
.pch-bt.verde { background: #16a34a; border-color: #16a34a; color: #fff; }
.pch-bt.verm { background: #fff; border-color: #fca5a5; color: #b91c1c; }
.pch-bt:disabled { opacity: .6; cursor: default; }
.pch-mini { border: 1px solid #cbd5e1; background: #fff; border-radius: 8px; padding: 5px 10px; font: inherit; font-size: .78rem; font-weight: 700; cursor: pointer; flex-shrink: 0; }
.pch-mini.tirar { border-color: #fca5a5; color: #b91c1c; }
`;

function selo(presente, origem, faltaJust) {
  if (presente) return <span className="pch-selo sim">✓ Presente{origem ? ` · ${ORIGEM[origem] || ''}` : ''}</span>;
  if (faltaJust) return <span className="pch-selo just">Falta justificada</span>;
  return <span className="pch-selo nao">Aguardando</span>;
}

function Placar({ total, presentes, rotulo, fase = 'abertura' }) {
  const est = useEstruturaSessao(); // PLENARIUM:QUORUM_CAMARA
  const q = quorumDaCamara(est, fase, quorum(total));
  const ok = presentes >= q;
  return (
    <div className="pch-placar">
      <div className="pch-num"><span>{rotulo}</span><b>{presentes} <small style={{ fontSize: '1rem', color: '#64748b' }}>de {total}</small></b></div>
      <div className={'pch-num ' + (ok ? 'ok' : 'no')}><span>Quórum (mínimo {q})</span><b>{ok ? 'Atingido' : 'Insuficiente'}</b></div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Secretario: so leitura
export function ChamadaSecretario({ sessao }) {
  const [d] = useChamadas(sessao && sessao.id);
  const [aba, setAba] = useState(null);
  if (!sessao) return <div className="pch-vazio">Nenhuma sessão em andamento.</div>;
  if (!d) return <div className="pch-vazio">Carregando…</div>;
  const temDuas = d.duas || !!d.segunda;
  const atual = aba || (d.segunda ? '2' : '1');
  const lista = d.lista || [];
  const n1 = lista.filter(p => p.p1).length;
  const n2 = lista.filter(p => p.p2).length;
  return (
    <div className="pch">
      <style>{CSS}</style>
      {temDuas && (
        <div className="pch-abas">
          <button type="button" className={'pch-aba' + (atual === '1' ? ' on' : '')} onClick={() => setAba('1')}>1ª chamada<small>Abertura da sessão · {n1} presente(s)</small></button>
          <button type="button" className={'pch-aba' + (atual === '2' ? ' on' : '')} onClick={() => setAba('2')}>
            {d.segunda && d.segunda.aberta && <span className="pch-vivo" />}2ª chamada
            <small>{!d.segunda ? 'Antes da Ordem do Dia · ainda não aberta' : d.segunda.aberta ? `Em andamento · ${n2} presente(s)` : `Encerrada · ${n2} presente(s)`}</small>
          </button>
        </div>
      )}
      {atual === '2' && !d.segunda ? (
        <div className="pch-vazio">A 2ª chamada ainda não foi aberta. Ela é feita antes da Ordem do Dia, quando o Presidente ou o administrador abrir. Esta tela atualiza sozinha.</div>
      ) : (
        <>
          <p className="pch-dica">Leia os nomes na ordem abaixo. A lista atualiza sozinha conforme os vereadores confirmam no tablet ou a Mesa registra a presença.{atual === '2' ? ' Quem não responder à 2ª chamada fica com falta na sessão.' : ''}</p>
          <Placar total={lista.length} presentes={atual === '2' ? n2 : n1} rotulo={atual === '2' ? 'Presentes na 2ª chamada' : temDuas ? 'Presentes na 1ª chamada' : 'Presentes'} fase={atual === '2' ? 'votacao' : 'abertura'} />
          <div className="pch-lista">
            {lista.map((p, i) => {
              const pres = atual === '2' ? p.p2 : p.p1;
              return (
                <div key={p.id} className={'pch-linha' + (pres ? ' pres' : '')}>
                  <span className="pch-n">{i + 1}</span>
                  <span className="pch-nome"><b>{p.nome}</b><small>{p.partido || ''}</small></span>
                  {selo(pres, atual === '2' ? p.o2 : p.o1, !pres && p.faltaJustificada)}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Painel da 2a chamada: admin (com marcacao manual) ou tablet do Presidente
export function PainelSegundaChamada({ sessaoId, modo = 'admin' }) {
  const [d, carregar] = useChamadas(sessaoId);
  const [ocupado, setOcupado] = useState(false);
  const admin = modo === 'admin';
  const hist = useHistoricoPresenca(sessaoId, admin, d); // PLENARIUM:PRESENCA_REGISTRO
  if (!d) return <div className="pch-vazio">Carregando…</div>;
  const lista = d.lista || [];
  const n1 = lista.filter(p => p.p1).length;
  const n2 = lista.filter(p => p.p2).length;
  const acao = async (fn, ok) => {
    setOcupado(true);
    try { await fn(); if (ok) toast.success(ok); await carregar(); }
    catch (e) { toast.error(erroDe(e, 'Não foi possível concluir.')); }
    finally { setOcupado(false); }
  };
  const abrir = () => acao(() => axios.post(`/api/chamadas/sessao/${sessaoId}/abrir`), '2ª chamada aberta: os tablets pedem a confirmação.');
  const encerrar = () => acao(() => axios.post(`/api/chamadas/sessao/${sessaoId}/encerrar`), '2ª chamada encerrada.');
  const desfazer = () => { if (window.confirm('Desfazer a 2ª chamada? As confirmações dela serão apagadas e a presença volta a ser só a da 1ª chamada.')) acao(() => axios.delete(`/api/chamadas/sessao/${sessaoId}`), '2ª chamada desfeita.'); };
  const marcar = (p) => { if (p.p2 && p.o2 === 'TABLET' && !window.confirm(`${p.nome} confirmou no tablet. Retirar a presença dele(a) na 2ª chamada?\n\nA confirmação original fica guardada no histórico.`)) return; acao(() => (p.p2 ? axios.delete(`/api/chamadas/sessao/${sessaoId}/manual/${p.id}`) : axios.post(`/api/chamadas/sessao/${sessaoId}/manual/${p.id}`))); }; // PLENARIUM:PRESENCA_REGISTRO
  const seg = d.segunda;
  return (
    <div className="pch">
      <style>{CSS}</style>
      <div className="pch-bts">
        {!seg && <button type="button" className="pch-bt verde" disabled={ocupado} onClick={abrir}><Unlock size={16} /> Abrir 2ª chamada</button>}
        {seg && seg.aberta && <button type="button" className="pch-bt" disabled={ocupado} onClick={encerrar}><Lock size={16} /> Encerrar 2ª chamada</button>}
        {seg && !seg.aberta && <button type="button" className="pch-bt" disabled={ocupado} onClick={abrir}><RotateCcw size={16} /> Reabrir</button>}
        {seg && admin && <button type="button" className="pch-bt verm" disabled={ocupado} onClick={desfazer}><X size={16} /> Desfazer</button>}
      </div>
      {!seg ? (
        <p className="pch-dica">Ao abrir, cada tablet pede ao vereador que confirme a presença de novo, e a TV mostra a 2ª chamada. Quem não responder fica com <b>falta na sessão</b>. A 2ª chamada se encerra sozinha quando a Ordem do Dia é iniciada.</p>
      ) : (
        <>
          <p className="pch-dica">{seg.aberta ? 'Em andamento: os tablets estão pedindo a confirmação.' : 'Encerrada.'} {admin ? 'Você pode registrar a presença de quem respondeu de viva voz.' : ''} Presentes na 1ª chamada: <b>{n1}</b>.</p>
          <Placar total={lista.length} presentes={n2} rotulo="Presentes na 2ª chamada" fase="votacao" />
          <div className="pch-lista">
            {lista.map((p, i) => (
              <div key={p.id} className={'pch-linha' + (p.p2 ? ' pres' : '')}>
                <span className="pch-n">{i + 1}</span>
                <span className="pch-nome"><b>{p.nome}</b><small>{p.partido || ''}{p.p1 ? '' : ' · ausente na 1ª chamada'}</small>{admin && <HistoricoPresenca itens={hist} vereadorId={p.id} chamada={2} />}</span>
                {selo(p.p2, p.o2, !p.p2 && p.faltaJustificada)}
                {admin && p.o2 !== 'VOTO' && (
                  <button type="button" className={'pch-mini' + (p.p2 ? ' tirar' : '')} disabled={ocupado} onClick={() => marcar(p)}>{p.p2 ? 'Remover' : 'Marcar presente'}</button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Admin: botao "2a chamada" ao lado de "Presenca" (so se a camara usa duas)
export function BotaoSegundaChamada({ sessao }) {
  const [info, setInfo] = useState(null);
  const [aberto, setAberto] = useState(false);
  const sid = sessao && sessao.id;
  const buscar = useCallback(() => {
    if (!sid) return;
    axios.get(`/api/chamadas/sessao/${sid}`).then(r => setInfo(r.data)).catch(() => {});
  }, [sid]);
  useEffect(() => { buscar(); }, [buscar]);
  useAoVivo(sid, sessao && sessao.camaraId, buscar);
  if (!sessao || !info || (!info.duas && !info.segunda)) return null;
  const vivo = info.segunda && info.segunda.aberta;
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={() => setAberto(true)} style={vivo ? { borderColor: '#dc2626', color: '#b91c1c' } : undefined}>
        <UserCheck size={14} /> 2ª chamada{vivo ? ' · aberta' : info.segunda ? ' · feita' : ''}
      </button>
      {aberto && (
        <div className="modal-overlay" onClick={() => setAberto(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <h2 className="modal-title"><UserCheck size={16} /> 2ª chamada de presença</h2>
              <button className="btn-modal-close" onClick={() => setAberto(false)}><X size={18} /></button>
            </div>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <PainelSegundaChamada sessaoId={sid} modo="admin" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Antes de iniciar a Ordem do Dia: avisa se a 2a chamada nao foi feita
export async function podeIniciarOrdemDoDia(sessao) {
  try {
    const r = await axios.get(`/api/chamadas/sessao/${sessao.id}`);
    if (r.data && r.data.duas && !r.data.segunda) {
      if (!window.confirm('A 2ª chamada ainda não foi feita (botão "2ª chamada").\n\nIniciar a Ordem do Dia mesmo assim?')) return false;
    }
  } catch (e) { /* sem a informacao, segue como antes */ }
  return confirmaQuorum(sessao.id, 'votacao'); // PLENARIUM:PRESENCA_REGISTRO -- quorum para votar
}

// ---------------------------------------------------------------------
// Tablet do vereador: pede a confirmacao da 2a chamada
export function AvisoSegundaChamada({ sessao }) {
  const [st, setSt] = useState(null);
  const [minimizado, setMinimizado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const sid = sessao && sessao.status === 'EM_ANDAMENTO' ? sessao.id : null;
  const buscar = useCallback(() => {
    if (!sid) { setSt(null); return; }
    axios.get(`/api/chamadas/minha/${sid}`).then(r => setSt(r.data)).catch(() => {});
  }, [sid]);
  useEffect(() => { buscar(); }, [buscar]);
  useAoVivo(sid, sessao && sessao.camaraId, buscar);
  const precisaConfirmar = !!(st && st.aberta && !st.presente);
  useEffect(() => { if (precisaConfirmar) setMinimizado(false); }, [precisaConfirmar]);
  if (!sid || !st || !st.aberta || st.presente) return null;
  const confirmar = async () => {
    setEnviando(true);
    try { await axios.post(`/api/chamadas/confirmar/${sid}`); toast.success('Presença confirmada na 2ª chamada!'); buscar(); }
    catch (e) { toast.error(erroDe(e, 'Não foi possível confirmar.')); }
    finally { setEnviando(false); }
  };
  if (minimizado) {
    return (
      <button type="button" onClick={() => setMinimizado(false)}
        style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', top: 12, zIndex: 9700, background: '#dc2626', color: '#fff', border: 0, borderRadius: 999, padding: '10px 18px', fontWeight: 800, fontSize: '0.95rem', boxShadow: '0 6px 20px rgba(220,38,38,.4)', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
        <UserCheck size={18} /> 2ª chamada: toque para confirmar presença
      </button>
    );
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9700, background: 'rgba(15,23,42,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, maxWidth: 460, width: '100%', padding: '28px 24px 20px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.35)', fontFamily: 'inherit' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><UserCheck size={32} color="#16a34a" /></div>
        <div style={{ fontSize: '.8rem', fontWeight: 800, letterSpacing: '.12em', color: '#dc2626' }}>2ª CHAMADA</div>
        <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#0f172a', margin: '4px 0 8px' }}>Confirme sua presença</div>
        <p style={{ color: '#475569', fontSize: '.95rem', margin: '0 0 18px' }}>A Mesa abriu a 2ª chamada antes da Ordem do Dia. Quem não responder fica com falta na sessão.</p>
        <button type="button" disabled={enviando} onClick={confirmar}
          style={{ width: '100%', background: '#16a34a', color: '#fff', border: 0, borderRadius: 14, padding: '16px', fontSize: '1.15rem', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
          <Check size={22} /> {enviando ? 'Confirmando…' : 'Estou presente'}
        </button>
        <button type="button" onClick={() => setMinimizado(true)} style={{ marginTop: 10, background: 'none', border: 0, color: '#64748b', fontWeight: 700, padding: 8, cursor: 'pointer', fontFamily: 'inherit' }}>Agora não</button>
      </div>
    </div>
  );
}
